import { ChangeDetectionStrategy, Component, input, model, OnInit, output } from "@angular/core";
import { uniqueID } from "@globals/helper-functions";

/**
 * Custom Accordion component. 2 important things to note when using:
 * 1. For the title of accordion, you can eiter use "title" input prop OR use a <bi-accordion-header> element
 * 2. The content of the accordion MUST be wrapped in an element defining a "content" template variable. So use like:
 *    <bi-accordion><div content>I am content!</div></bi-accordion>
 */
@Component({
  selector: "bi-accordion",
  templateUrl: "./bi-accordion.component.html",
  styleUrls: ["./bi-accordion.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
  host: { "(keyup)": "onKeyUp($event)" }
})
export class BiAccordionComponent implements OnInit {
  title = input<string>();
  disabledTitle = input<string>();

  /**
   * Wether the accordion has a checkbox to the left in the title area. if false or not set, there will never a checkbox
   */
  hasLeftCb = input<boolean>();

  leftCbDisabled = input<boolean>();

  /**
   * Wether the accordion is disabled or not. When disabled, the arrow icon isn't shown and expansion disabled
   */
  disabled = input<boolean>();

  /**
   * Set this to a font-awesome icon class and this will be displayed left to title text.
   */
  titleIconClass = input<string>();

  titleColor = input<string>("#ffffff");
  titleFontColor = input<string>("#4B4B4B");
  titleFont = input<string>("inherit");
  titleFontSize = input<string>("inherit");
  contentBackgroundColor = input<string>("#ffffff");
  borderColor = input<string>("#333333");

  /**
   * Whether a padding will be used for the content inside the accordion. Default true.
   */
  hasContentPadding = input<boolean>(true);

  checked = model<boolean>();

  onOpen = output();
  onClose = output();

  /**
   * Wether the accordion content is expanded or not
   */
  isExpanded = model<boolean>();
  alwaysExpanded = input(false);

  public titleCss: { [style: string]: string } = {};

  public uniqueId = uniqueID(); // for having a unique id for HTML input elements and their associated labels.

  public ngOnInit() {
    this.setStyling();

    if (this.checked()) this.setExpansion(true);
  }

  public setExpansion(expand: boolean) {
    if (this.disabled()) return;

    if (!this.alwaysExpanded()) this.isExpanded.set(expand);
  }

  public emitOpenOrClose(expand: boolean) {
    if (this.disabled()) return;

    if (expand) this.onOpen.emit();
    else this.onClose.emit();
  }

  public setStyling() {
    if (this.titleColor()) this.titleCss["background-color"] = this.titleColor();
    if (this.titleFontColor()) this.titleCss["color"] = this.titleFontColor();
    if (this.titleFontSize()) this.titleCss["font-size"] = this.titleFontSize() + "px";
    // Font family is set inside template
  }

  public onKeyUp(e: KeyboardEvent) {
    if ((e.target as HTMLElement).nodeName !== "BI-ACCORDION") return;

    if (e.key === "ArrowDown" && !this.isExpanded()) this.setExpansion(true);
    if (e.key === "ArrowUp" && this.isExpanded()) this.setExpansion(false);
  }

  public onHeaderClicked() {
    if (this.alwaysExpanded()) return;
    const newState = !this.isExpanded();
    this.isExpanded.set(newState);
    this.emitOpenOrClose(newState);
  }
}
